import {
  DatiAggiuntiviSide,
  LuogoCelebrazione,
  ModelEventoDTO,
  SubjectAnpr,
} from 'shell/models/act-category-list';
import {
  AccordionMarriage,
  SearchMarriageFormVM,
} from 'mutation/view-models/registration-marriage';
import { DateTime } from 'luxon';
import { defaultChoicePickerState } from 'shell/components/shared/choice-date/constants';
import { ActType } from 'shell/constants/act-type';
import { CODE_STATE_ITALIAN } from 'shell/constants/code-state-italian';
import { ItalianForm } from 'shell/view-models/italian-form';
import { MUNICIPALITY_MILANO } from 'civil-status/constants/municipality-milano';

const transformPerson = (subjectAnpr: SubjectAnpr) => {
  return {
    fiscalCode: subjectAnpr.codiceFiscale || '',
    surname: subjectAnpr.cognome || '',
    name: subjectAnpr.nome || '',
  };
};

const transformGenerality = (subjectAnpr: SubjectAnpr) => {
  const isForeign = subjectAnpr.idStatoNascita !== CODE_STATE_ITALIAN;

  return {
    anprIdentifier: subjectAnpr.idsoggettoanpr || '',
    gender: subjectAnpr.sesso || '',
    birthDate: subjectAnpr.dataNascita ? DateTime.fromISO(subjectAnpr.dataNascita) : null,
    birthChoiceDate: subjectAnpr.dataNascita ? defaultChoicePickerState : undefined,
    aire: subjectAnpr.idComuneAIRE ? 'S' : 'N',
    isForeign,
    italian: isForeign
      ? undefined
      : {
          municipality: {
            description: subjectAnpr.nomeComuneNascita || '',
            province: subjectAnpr.siglaProvinciaNascita || '',
            code: subjectAnpr.idComuneNascita
              ? Number.parseInt(subjectAnpr.idComuneNascita.toString(), 10)
              : undefined,
            id: subjectAnpr.idComuneNascita || '',
            idProvince: subjectAnpr.idProvinciaNascita
              ? Number.parseInt(subjectAnpr.idProvinciaNascita.toString(), 10)
              : undefined,
            provinceCode: subjectAnpr.idProvinciaNascita || '',
          },
        },
    foreign: isForeign
      ? {
          state: {
            id: subjectAnpr.idStatoNascita || '',
            value: subjectAnpr.nomeStatoNascita || '',
          },
          municipality: {
            description: subjectAnpr.nomeComuneNascita || subjectAnpr.localitaEsteraNascita || '',
            province: subjectAnpr.siglaProvinciaNascita || '',
            id: subjectAnpr.idComuneNascita || '',
            idProvince: subjectAnpr.idProvinciaNascita
              ? Number.parseInt(subjectAnpr.idProvinciaNascita.toString(), 10)
              : undefined,
            provinceCode: subjectAnpr.idProvinciaNascita || '',
          },
        }
      : undefined,
  };
};

const transformMarriageItalian = (
  datiAggiuntiviSide: DatiAggiuntiviSide,
  isForeign: boolean,
  isCasaComunale: boolean,
  luogoCelebrazioneTranscod?: LuogoCelebrazione
): ItalianForm | undefined => {
  if (isForeign) {
    return undefined;
  }

  if (luogoCelebrazioneTranscod) {
    return {
      municipality: {
        description:
          luogoCelebrazioneTranscod?.nomeComune || datiAggiuntiviSide.nomeComuneRegistrazione || '',
        province:
          luogoCelebrazioneTranscod?.siglaProvincia ||
          datiAggiuntiviSide.siglaProvinciaRegistrazione ||
          '',
        code:
          luogoCelebrazioneTranscod?.idComune || datiAggiuntiviSide.idComuneRegistrazione
            ? Number.parseInt(
                luogoCelebrazioneTranscod?.idComune ||
                  datiAggiuntiviSide.idComuneRegistrazione ||
                  '',
                10
              )
            : undefined,
        id: luogoCelebrazioneTranscod?.idComune || datiAggiuntiviSide.idComuneRegistrazione || '',
        idProvince:
          luogoCelebrazioneTranscod?.idProvincia || datiAggiuntiviSide.idProvinciaRegistrazione
            ? Number.parseInt(
                luogoCelebrazioneTranscod?.idProvincia ||
                  datiAggiuntiviSide.idProvinciaRegistrazione ||
                  '',
                10
              )
            : undefined,
        provinceCode:
          luogoCelebrazioneTranscod?.idProvincia ||
          datiAggiuntiviSide.idProvinciaRegistrazione ||
          '',
      },
    };
  }

  if (isCasaComunale) {
    return {
      municipality: MUNICIPALITY_MILANO,
    };
  }
};

export const transformMarriageAccordion = (
  subjectAnpr: SubjectAnpr,
  modelEventoDTO: ModelEventoDTO,
  datiAggiuntiviSide: DatiAggiuntiviSide
): { marriage?: AccordionMarriage; searchMarriage?: SearchMarriageFormVM } => {
  const datiEventoMatrimonio = modelEventoDTO.datiEventoMatrimonio || {};

  const { luogoCelebrazioneTranscod } = datiAggiuntiviSide;

  // se redatto in casa comunale non abbiamo il luogo celebrazione ma è di default Milano
  const isCasaComunale = modelEventoDTO.redattoNellaCasaComunale || false;

  const isForeign =
    luogoCelebrazioneTranscod && luogoCelebrazioneTranscod.idStato
      ? luogoCelebrazioneTranscod.idStato !== CODE_STATE_ITALIAN
      : false;

  const isWithSearchMarriage = !subjectAnpr.idsoggettoanpr;

  return {
    searchMarriage: isWithSearchMarriage
      ? {
          fiscalCode: subjectAnpr.codiceFiscale || '',
          surname: subjectAnpr.cognome || '',
          name: subjectAnpr.nome || '',
          gender: subjectAnpr.sesso || '',
          birthday: subjectAnpr.dataNascita ? DateTime.fromISO(subjectAnpr.dataNascita) : null,
          birthdayChoice: defaultChoicePickerState,
        }
      : undefined,
    marriage: {
      idAnprSoggetto: subjectAnpr.idsoggettoanpr || undefined,
      person: transformPerson(subjectAnpr),
      generality: transformGenerality(subjectAnpr),
      eventChoiceDate: datiEventoMatrimonio.dataEvento ? defaultChoicePickerState : undefined,
      eventDate: datiEventoMatrimonio.dataEvento
        ? DateTime.fromISO(datiEventoMatrimonio.dataEvento)
        : undefined,
      isForeign,
      italian: transformMarriageItalian(
        datiAggiuntiviSide,
        isForeign,
        isCasaComunale,
        luogoCelebrazioneTranscod
      ),
      foreign: isForeign
        ? {
            state: luogoCelebrazioneTranscod?.idStato
              ? {
                  id: luogoCelebrazioneTranscod?.idStato,
                  value: luogoCelebrazioneTranscod.nomeStato || '',
                }
              : undefined,
            municipality: {
              description: luogoCelebrazioneTranscod?.nomeComune || '',
              province: luogoCelebrazioneTranscod?.siglaProvincia || '',
              id: luogoCelebrazioneTranscod?.idComune || '',
              idProvince: luogoCelebrazioneTranscod?.idProvincia
                ? Number.parseInt(luogoCelebrazioneTranscod.idProvincia.toString(), 10)
                : undefined,
              provinceCode: luogoCelebrazioneTranscod?.idProvincia || '',
            },
          }
        : undefined,
      actType: ActType.AttoDigitale,
      digital: {
        documentDate: modelEventoDTO.dataformazione
          ? DateTime.fromISO(modelEventoDTO.dataformazione)
          : null,
        istatCode: datiAggiuntiviSide.idComuneRegistrazione || '',
        municipality: datiAggiuntiviSide.idComuneRegistrazione
          ? {
              id: datiAggiuntiviSide.idComuneRegistrazione || '',
              description: datiAggiuntiviSide.nomeComuneRegistrazione || '',
              idProvince: datiAggiuntiviSide.idProvinciaRegistrazione
                ? Number.parseInt(datiAggiuntiviSide.idProvinciaRegistrazione, 10)
                : undefined,
              province: datiAggiuntiviSide.siglaProvinciaRegistrazione || '',
            }
          : undefined,
        idAnsc: modelEventoDTO.idAnsc,
        year: modelEventoDTO.idAnsc ? modelEventoDTO.idAnsc.split('-')[0] : '',
        nationalNumber: modelEventoDTO.idAnsc ? modelEventoDTO.idAnsc.split('-')[1] : '',
        numberMunicipality: modelEventoDTO.idAnsc ? modelEventoDTO.idAnsc.split('-')[2] : '',
      },
    },
  };
};
